import React from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";
import { SeverityStyle } from "./severity";

/**
 * Central segmented (two-plus-way) toggle for the Tractor State feature — the
 * one place its look, behaviour, and focus ring are defined, so every use
 * (name toggle, rail sort, short enum fields) is identical. Generic over the
 * option value so callers keep their own string-literal types.
 *
 * Two roles, one control. As a *view* toggle (name mode, rail sort) it is a
 * group of pressed buttons. As a *form* control for an enum with a handful of
 * options it is a radiogroup, labelled by the field's label, and each segment
 * can carry the option's own severity colour and glyph — so choosing "✓ Yes"
 * over "✕ No" looks the same in the form as the chosen value does in the
 * record, instead of collapsing to a dropdown that reads "Yes".
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Rendered before the label; the option's glyph, when it has one. */
  glyph?: string;
  /** Colours for the *selected* state. Defaults to the blue fill. */
  tone?: SeverityStyle;
  /** Explains the option; shown as a tooltip on the segment. */
  help?: string;
  /**
   * Refused for this row right now (a cross-field rule). The segment still
   * renders — the vocabulary does not shrink — but cannot be chosen.
   */
  disabled?: boolean;
  /** Why it is refused; shown as the segment's tooltip in place of `help`. */
  disabledReason?: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  /** For form use: the id of the visible label. */
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  /** `radiogroup` for a form field, `group` (default) for a view toggle. */
  role?: "group" | "radiogroup";
  id?: string;
  invalid?: boolean;
  /** Segments share the width — for a field in the record's column grid. */
  fill?: boolean;
  onBlur?: () => void;
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  role = "group",
  id,
  invalid = false,
  fill = false,
  onBlur,
}: SegmentedControlProps<T>) {
  const radio = role === "radiogroup";
  return (
    <Row
      id={id}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-invalid={radio && invalid ? true : undefined}
      $invalid={invalid}
      $fill={fill}
      onBlur={(e) => {
        // Leaving the whole control, not moving between its segments.
        if (onBlur && !e.currentTarget.contains(e.relatedTarget as Node)) {
          onBlur();
        }
      }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <Seg
            key={o.value}
            type="button"
            role={radio ? "radio" : undefined}
            aria-checked={radio ? active : undefined}
            aria-pressed={radio ? undefined : active}
            aria-disabled={o.disabled || undefined}
            disabled={o.disabled}
            title={o.disabled ? (o.disabledReason ?? o.help) : o.help}
            $active={active}
            $tone={o.tone}
            $fill={fill}
            $disabled={Boolean(o.disabled)}
            onClick={() => onChange(o.value)}
          >
            {o.glyph && <Glyph aria-hidden="true">{o.glyph}</Glyph>}
            {o.label}
          </Seg>
        );
      })}
    </Row>
  );
}

const Row = styled.div<{ $invalid: boolean; $fill: boolean }>`
  display: ${(p) => (p.$fill ? "flex" : "inline-flex")};
  flex-direction: row;
  flex-wrap: ${(p) => (p.$fill ? "wrap" : "nowrap")};
  width: ${(p) => (p.$fill ? "100%" : "auto")};
  box-sizing: border-box;
  background: ${tokens.white};
  border: 1px solid ${(p) => (p.$invalid ? tokens.groundedDot : tokens.line)};
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
`;

const Seg = styled.button<{
  $active: boolean;
  $tone?: SeverityStyle;
  $fill: boolean;
  $disabled: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: ${(p) => (p.$fill ? "1 1 auto" : "none")};
  padding: 7px 14px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  transition:
    background-color 0.15s,
    color 0.15s,
    border-color 0.15s;
  background: ${(p) =>
    p.$active ? (p.$tone ? p.$tone.bg : tokens.blue) : "transparent"};
  color: ${(p) =>
    p.$active ? (p.$tone ? p.$tone.color : tokens.white) : tokens.slate};
  border-color: ${(p) =>
    p.$active && p.$tone ? p.$tone.border : "transparent"};
  /* Refused, not hidden: still legible, visibly not on offer. */
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    color: ${(p) =>
      p.$active ? (p.$tone ? p.$tone.color : tokens.white) : tokens.blue};
  }

  /* Kill the UA's rectangular outline; show a radius-matching ring (outline
     follows the 6px corners in modern browsers) for keyboard focus. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Glyph = styled.span`
  font-size: 11px;
  line-height: 1;
`;

export default SegmentedControl;
